import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../../config';
import { Typography, Card, IconButton, Button, CircularProgress, Chip, Tooltip } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DoseRecordDialog from './DoseRecordDialog';
import { EMAR_STATUS, clientClock, readApiError } from '../../../utils/emarStatus';

const DATE_FMT = 'YYYY-MM-DD';

/** One line under a dose saying what was recorded, by whom, and when. */
function recordLine(dose) {
    const by = dose.markedByName ? ` by ${dose.markedByName}` : '';

    if (dose.status === 'onTime' || dose.status === 'late') {
        const late = dose.status === 'late' ? ` (${dose.minutesLate} min late)` : '';
        const outcome = dose.outcomeLabel ? ` · ${dose.outcomeLabel}` : '';
        return `Given ${dose.takenAtDisplay}${late}${by}${outcome}`;
    }
    if (dose.status === 'notGiven') {
        return `Not given${dose.reasonLabel ? ` — ${dose.reasonLabel}` : ''}${by}`;
    }
    return null;
}

/**
 * Health tab card for charting doses: every dose scheduled on the chosen day (today by
 * default), what has been recorded against it, and a Record / Edit action.
 *
 * The list is the same day's column of the eMAR (FWAPI GET /emar/day), so it can never
 * disagree with the Care Management > eMAR report about what was due.
 */
function MedicationAdministrationSection({ customer_id, empId, medications }) {
    const [date, setDate] = useState(moment().format(DATE_FMT));
    const [day, setDay] = useState(null);
    const [codes, setCodes] = useState({ outcome: [], reason: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeDose, setActiveDose] = useState(null);

    const today = moment().format(DATE_FMT);

    useEffect(() => {
        axios.get(APIURL + '/emar/intake-codes', { params: { emp_id: empId } })
            .then(res => setCodes({ outcome: res.data?.outcome || [], reason: res.data?.reason || [] }))
            .catch(err => console.log(err));
    }, [empId]);

    const fetchDay = useCallback(async () => {
        if (!customer_id) return;

        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(APIURL + '/emar/day', {
                params: { cus_id: customer_id, date, emp_id: empId, ...clientClock() },
            });
            setDay(res.data);
        } catch (err) {
            console.log(err);
            setDay(null);
            setError(readApiError(err, 'Could not load doses.'));
        }
        setLoading(false);
    }, [customer_id, date, empId]);

    // Re-read when the medication list changes too: adding, stopping or editing a
    // medication changes which doses are due.
    useEffect(() => {
        fetchDay();
    }, [fetchDay, medications]);

    const shiftDay = (delta) => {
        const next = moment(date, DATE_FMT).add(delta, 'day');
        if (next.isAfter(moment(), 'day')) return;
        setDate(next.format(DATE_FMT));
    };

    const doses = day?.doses || [];

    return (
        <Card elevation={3} style={{ marginBottom: 5, padding: 12, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                <Typography variant="subtitle1" fontWeight="bold">MEDICATION ADMINISTRATION</Typography>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton size="small" onClick={() => shiftDay(-1)} aria-label="Previous day">
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            value={moment(date, DATE_FMT)}
                            onChange={(value) => { if (value && value.isValid()) setDate(value.format(DATE_FMT)); }}
                            maxDate={moment()}
                            format="MM/DD/YYYY"
                            slotProps={{ textField: { size: 'small', style: { width: 160 }, inputProps: { 'aria-label': 'Dose date' } } }}
                        />
                    </LocalizationProvider>
                    <IconButton size="small" onClick={() => shiftDay(1)} disabled={date >= today} aria-label="Next day">
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading && !day && (
                    <div className="d-flex justify-content-center" style={{ marginTop: 12 }}><CircularProgress size={24} /></div>
                )}
                {error && <Typography variant="body2" color="error">{error}</Typography>}
                {!loading && !error && doses.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                        No doses scheduled {date === today ? 'today' : `on ${moment(date, DATE_FMT).format('MM/DD/YYYY')}`}.
                    </Typography>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {doses.map(dose => {
                        const style = EMAR_STATUS[dose.status] || EMAR_STATUS.upcoming;
                        const recorded = recordLine(dose);

                        return (
                            <div
                                key={`${dose.customer_medication_id}-${dose.time}`}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', border: '1px solid #e8e8e8', borderRadius: 4, background: '#fafafa' }}
                            >
                                <Typography variant="body2" style={{ fontWeight: 700, minWidth: 64 }}>{dose.displayTime}</Typography>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" style={{ fontWeight: 500 }} noWrap>
                                        {dose.name}{dose.dosage ? ` — ${dose.dosage}` : ''}
                                    </Typography>
                                    {recorded && (
                                        <Tooltip title={dose.notes || ''} disableHoverListener={!dose.notes}>
                                            <Typography variant="caption" color="textSecondary" noWrap component="div">{recorded}</Typography>
                                        </Tooltip>
                                    )}
                                </div>
                                <Chip
                                    size="small"
                                    label={style.short}
                                    style={{ background: style.color, color: style.text, fontWeight: 600 }}
                                />
                                <Button size="small" variant="outlined" onClick={() => setActiveDose(dose)} style={{ minWidth: 72 }}>
                                    {recorded ? 'Edit' : 'Record'}
                                </Button>
                            </div>
                        );
                    })}
                </div>

                {day?.unscheduled?.length > 0 && (
                    <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 8 }}>
                        Not on a schedule: {day.unscheduled.map(m => m.name).join(', ')}
                    </Typography>
                )}
            </div>

            <DoseRecordDialog
                dose={activeDose}
                codes={codes}
                empId={empId}
                onClose={() => setActiveDose(null)}
                onSaved={() => { setActiveDose(null); fetchDay(); }}
            />
        </Card>
    );
}

export default MedicationAdministrationSection;
